import React, { useState, useEffect } from "react";
import { useAuth } from "../../lib/auth";
import { useContent } from "../../lib/content";
import { Field, TextArea, LinesField, ImageField, Msg, Section, IconSelect } from "../../components/admin/FormKit";
import { DEFAULT_LANDING } from "../../lib/seed";
import { Save, Home, Sparkles, Plus, Trash2, RotateCcw } from "lucide-react";

export default function AdminLanding() {
  const { r2 } = useAuth();
  const { landing, saveLanding } = useContent();
  const [form, setForm] = useState(landing);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setForm({
      ...DEFAULT_LANDING,
      ...landing,
      hero: { ...DEFAULT_LANDING.hero, ...(landing.hero || {}) },
      our_story: { ...DEFAULT_LANDING.our_story, ...(landing.our_story || {}) },
      categories_section: { ...DEFAULT_LANDING.categories_section, ...(landing.categories_section || {}) },
      featured_section: { ...DEFAULT_LANDING.featured_section, ...(landing.featured_section || {}) },
      articles_section: { ...DEFAULT_LANDING.articles_section, ...(landing.articles_section || {}) },
      cta_band: { ...DEFAULT_LANDING.cta_band, ...(landing.cta_band || {}) },
      trust_badges: landing.trust_badges || DEFAULT_LANDING.trust_badges,
    });
  }, [landing]);

  const setHero = (patch) => setForm((f) => ({ ...f, hero: { ...f.hero, ...patch } }));
  const setStory = (patch) => setForm((f) => ({ ...f, our_story: { ...f.our_story, ...patch } }));
  const setSection = (key, patch) => setForm((f) => ({ ...f, [key]: { ...f[key], ...patch } }));

  // Trust badges
  const updateBadge = (i, patch) => {
    setForm((f) => {
      const arr = [...(f.trust_badges || [])];
      arr[i] = { ...arr[i], ...patch };
      return { ...f, trust_badges: arr };
    });
  };
  const addBadge = () => setForm((f) => ({ ...f, trust_badges: [...(f.trust_badges || []), { label: "", icon: "Leaf" }] }));
  const removeBadge = (i) => setForm((f) => ({ ...f, trust_badges: (f.trust_badges || []).filter((_, k) => k !== i) }));

  // Story body lines
  const setStoryBody = (lines) => setStory({ body: lines });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg("");
    try {
      await saveLanding(r2, form);
      setMsg("صفحه اصلی ذخیره شد ✓");
    } catch (e) {
      setMsg(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    if (!window.confirm("بازنشانی به مقادیر پیش‌فرض؟ (ذخیره نمی‌شود تا روی دکمه بزنید)")) return;
    setForm(DEFAULT_LANDING);
  };

  return (
    <form onSubmit={submit} className="space-y-10" data-testid="admin-landing-panel">
      <Section title="بخش هیرو (Hero)" icon={Home} desc="متن اصلی و تصویر پس‌زمینه بالای صفحه اصلی">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="نشان (badge) بالای تیتر" value={form.hero.badge} onChange={(v) => setHero({ badge: v })} full testid="admin-hero-badge" />
          <Field label="تیتر — خط بالا" value={form.hero.title_top} onChange={(v) => setHero({ title_top: v })} testid="admin-hero-title-top" />
          <Field label="تیتر — خط هایلایت" value={form.hero.title_highlight} onChange={(v) => setHero({ title_highlight: v })} testid="admin-hero-title-hl" />
          <TextArea label="زیرتیتر" value={form.hero.subtitle} onChange={(v) => setHero({ subtitle: v })} full testid="admin-hero-subtitle" rows={3} />
          <Field label="دکمه اول (Primary CTA)" value={form.hero.cta_primary} onChange={(v) => setHero({ cta_primary: v })} testid="admin-hero-cta1" />
          <Field label="دکمه دوم (WhatsApp CTA)" value={form.hero.cta_secondary} onChange={(v) => setHero({ cta_secondary: v })} testid="admin-hero-cta2" />
          <ImageField
            label="تصویر پس‌زمینه هیرو"
            value={form.hero.background_image}
            onChange={(v) => setHero({ background_image: v })}
            full
            testid="admin-hero-bg"
            hint="پیشنهاد: تصویر با عرض ۲۰۰۰ پیکسل و نسبت افقی"
          />
        </div>
      </Section>

      <Section title="نشان‌های اعتماد" icon={Sparkles} desc="زیر دکمه‌های هیرو نمایش داده می‌شود">
        <div className="space-y-3">
          {(form.trust_badges || []).map((b, i) => (
            <div key={i} className="grid grid-cols-12 gap-3 items-end">
              <div className="col-span-12 md:col-span-7">
                <Field label={i === 0 ? "متن" : ""} value={b.label} onChange={(v) => updateBadge(i, { label: v })} testid={`admin-badge-label-${i}`} />
              </div>
              <div className="col-span-8 md:col-span-4">
                <IconSelect label={i === 0 ? "آیکون" : ""} value={b.icon} onChange={(v) => updateBadge(i, { icon: v })} testid={`admin-badge-icon-${i}`} />
              </div>
              <div className="col-span-4 md:col-span-1">
                <button type="button" onClick={() => removeBadge(i)} className="w-full h-[42px] grid place-items-center bg-red-50 text-red-600 rounded-xl hover:bg-red-100" data-testid={`admin-badge-remove-${i}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          <button type="button" onClick={addBadge} className="inline-flex items-center gap-1 text-honey-700 hover:text-honey-800 text-sm" data-testid="admin-badge-add">
            <Plus className="w-4 h-4" /> افزودن نشان
          </button>
        </div>
      </Section>

      <Section title="بخش دسته‌بندی‌ها (تیتر)" icon={Home} desc="عنوان و زیرتیتر بالای گرید دسته‌بندی‌ها">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Eyebrow (بالای تیتر)" value={form.categories_section.eyebrow} onChange={(v) => setSection("categories_section", { eyebrow: v })} />
          <Field label="عنوان بخش" value={form.categories_section.title} onChange={(v) => setSection("categories_section", { title: v })} />
          <TextArea full label="زیرتیتر" rows={2} value={form.categories_section.subtitle} onChange={(v) => setSection("categories_section", { subtitle: v })} />
        </div>
      </Section>

      <Section title="بخش محصولات ویژه (تیتر)">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Eyebrow" value={form.featured_section.eyebrow} onChange={(v) => setSection("featured_section", { eyebrow: v })} />
          <Field label="عنوان بخش" value={form.featured_section.title} onChange={(v) => setSection("featured_section", { title: v })} />
        </div>
      </Section>

      <Section title="بخش «داستان ما»" icon={Home}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Eyebrow" value={form.our_story.eyebrow} onChange={(v) => setStory({ eyebrow: v })} />
          <Field label="عنوان — خط بالا" value={form.our_story.title_top} onChange={(v) => setStory({ title_top: v })} />
          <Field label="عنوان — خط پایین (هایلایت)" value={form.our_story.title_bottom} onChange={(v) => setStory({ title_bottom: v })} />
          <Field label="برچسب دکمه" value={form.our_story.cta_label} onChange={(v) => setStory({ cta_label: v })} />
          <LinesField full label="پاراگراف‌های متن (هر خط = یک پاراگراف)" rows={6} value={form.our_story.body} onChange={setStoryBody} />
          <ImageField full label="تصویر کنار متن داستان" value={form.our_story.image} onChange={(v) => setStory({ image: v })} testid="admin-story-image" />
        </div>
      </Section>

      <Section title="بخش مقالات (تیتر)">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Eyebrow" value={form.articles_section.eyebrow} onChange={(v) => setSection("articles_section", { eyebrow: v })} />
          <Field label="عنوان بخش" value={form.articles_section.title} onChange={(v) => setSection("articles_section", { title: v })} />
        </div>
      </Section>

      <Section title="باند CTA پایین صفحه">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="عنوان" value={form.cta_band.title} onChange={(v) => setSection("cta_band", { title: v })} full />
          <TextArea label="متن" value={form.cta_band.body} onChange={(v) => setSection("cta_band", { body: v })} full rows={3} />
          <Field label="برچسب دکمه (WhatsApp)" value={form.cta_band.cta_label} onChange={(v) => setSection("cta_band", { cta_label: v })} />
        </div>
      </Section>

      <div className="flex items-center justify-between sticky bottom-4 bg-paper/95 backdrop-blur border border-honey-200 rounded-full px-4 py-2 shadow-lg">
        <div className="flex items-center gap-3">
          <button type="button" onClick={reset} className="inline-flex items-center gap-1 text-inkMuted hover:text-ink text-sm">
            <RotateCcw className="w-4 h-4" /> بازنشانی
          </button>
          <Msg msg={msg} />
        </div>
        <button
          type="submit"
          disabled={busy}
          className="inline-flex items-center gap-2 bg-honey-500 hover:bg-honey-600 text-white font-display font-bold px-5 py-2.5 rounded-full disabled:opacity-60"
          data-testid="admin-landing-save"
        >
          <Save className="w-4 h-4" /> {busy ? "در حال ذخیره..." : "ذخیره صفحه اصلی"}
        </button>
      </div>
    </form>
  );
}
